"use client";

import { ArrowLeftIcon, Loader2Icon, UserIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { broadcastSessionUpdate } from "@/lib/auth/broadcastChannel";
import { endImpersonation } from "@/server-actions/impersonation/impersonationService";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

export default function ImpersonationBanner() {
  const { data: session, update } = useSession();
  const router = useRouter();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // If there's no impersonation active, just return
  if (!session?.user?.isImpersonated) {
    return null;
  }

  const handleEndImpersonation = async () => {
    if (loading) return;
    setLoading(true);

    try {
      // End impersonation and get original user data
      const originalUser = await endImpersonation();

      if (!originalUser) {
        console.error("Failed to get original user data");
        setLoading(false);
        return;
      }

      
      // Update the session with original user data
      await update(originalUser);
      
      // Broadcast the session update to all tabs
      broadcastSessionUpdate();

      // Navigate to a safe destination after ending impersonation
      router.replace("/");
      router.refresh();

      toast({
        title: "Success",
        description: "Returned to your original user",
      });
    } catch (error) {
      console.error("Failed to end impersonation:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to return to original session",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-800",
      supervisor: "bg-action-100 text-action-800",
      user: "bg-gray-100 text-gray-800",
      default: "bg-gray-100 text-gray-800",
    };
    return colors[role?.toLowerCase()] || colors.default;
  };

  return (
    <div className="bg-yellow-50 p-2 text-sm sticky top-0 z-50">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <UserIcon className="h-4 w-4 text-yellow-600" />
          <span>
            Impersonating <strong>{session.user.name}</strong>
            {session.user.role && (
              <span
                className={`ml-2 px-1.5 py-0.5 text-xs font-medium rounded ${getRoleBadgeColor(
                  session.user.role
                )}`}
              >
                {session.user.role}
              </span>
            )}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEndImpersonation}
          disabled={loading}
          className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100"
        >
          {loading ? (
            <>
              <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              Returning...
            </>
          ) : (
            <>
              <ArrowLeftIcon className="mr-2 h-4 w-4" />
              Return to original user
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
